import { useMutation, useQuery } from '@tanstack/react-query';
import { showToast } from 'app/shared/util/toast.util';
import { queryClient } from 'app/app';
import { toast } from 'react-toastify';
import {
  copyApplicants,
  createEGradaninInitialData,
  deleteApplicant,
  fetchApplicants,
  fetchApplicantsForAssignee,
  getApplicantsNumber, getApplicantsVariant,
  getCaseApplicant,
  saveApplicant,
  saveApplicantsNumber
} from 'app/edozvola/cases/case-applicant/service/case-applicant.service';
import { CaseApplicantModel } from 'app/edozvola/cases/case-applicant/model/case-applicant.model';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import success = toast.success;
import error = toast.error;
import { CodebookModel } from 'app/shared/model/shared.model';
import { messages, TABLE_QUERY_KEYS } from 'app/config/constants';

export const saveApplicantQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (applicant: CaseApplicantModel) => {
      return saveApplicant(idCase, applicant);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.APPLICANTS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchApplicantsQuery = (pagination: PaginationState, idCase: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.APPLICANTS_TABLE, { idCase: idCase, pagination: { ...pagination } }],
    queryFn: ({ signal }) => fetchApplicants(idCase, pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const fetchCaseApplicantQuery = (idCaseApplicant: string) => {
  return useQuery({
    queryKey: ['case-applicant', idCaseApplicant],
    queryFn: () => getCaseApplicant(idCaseApplicant),
    enabled: !!idCaseApplicant,
  });
};

export const createEGradaninApplicantInitialDataQuery = (isEGradanin: boolean, idUser: number) => {
  return useQuery({
    queryKey: ['case-applicant-initial', idUser],
    queryFn: () => createEGradaninInitialData(idUser),
    enabled: isEGradanin,
  });
};

export const fetchApplicantsForAssigneeQuery = (idCase: string, shouldFetch: boolean, idApplicant: string) => {
  return useQuery({
    queryKey: ['applicants-for-assignee', idCase],
    queryFn: () => fetchApplicantsForAssignee(idCase, idApplicant),
    enabled: shouldFetch,
  });
};

export const deleteApplicantQuery = () => {
  return useMutation({
    mutationFn: (idApplicant: number) => deleteApplicant(idApplicant),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.APPLICANTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyApplicantsFromCaseQuery = (caseUuid: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (caseUuidToCopyFrom: string) => copyApplicants(caseUuid, caseUuidToCopyFrom),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.APPLICANTS_TABLE] });
      showToast(success, messages.DATA_COPIED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const saveApplicantsNumberQuery = (caseUuid: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (applicantsNumber: CodebookModel[]) => saveApplicantsNumber(caseUuid, applicantsNumber),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: ['applicants-number', caseUuid] });
      showToast(success, messages.DATA_SAVED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchApplicantsNumberQuery = (idCase: string) => {
  return useQuery({
    queryKey: ['applicants-number', idCase],
    queryFn: () => getApplicantsNumber(idCase),
  });
};

export const fetchApplicantsVariantQuery = (idCase: string, caseInPreview: boolean) => {
  return useQuery({
    queryKey: ['applicants-variant', { idCase: idCase, caseInPreview: caseInPreview }],
    queryFn: () => getApplicantsVariant(idCase, caseInPreview),
  });
};
